import React from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Target, Rocket, ChartLine, CheckCircle } from '@phosphor-icons/react';

const SeoProcess = () => {
  const processSteps = [
    {
      step: "01",
      icon: <MagnifyingGlass className="w-8 h-8" weight="thin" />,
      title: "SEO Audit & Analyse",
      description: "We analyseren je huidige SEO-prestaties, identificeren verbeterpunten en brengen je concurrentie in kaart.",
      duration: "2-3 dagen",
      deliverables: ["SEO Audit Rapport", "Keyword Analyse", "Competitor Analyse", "Technische SEO Check"]
    },
    {
      step: "02",
      icon: <Target className="w-8 h-8" weight="thin" />,
      title: "SEO Strategie & Planning",
      description: "Op basis van de audit ontwikkelen we een op maat gemaakte SEO-strategie die aansluit bij jouw doelen.",
      duration: "3-5 dagen",
      deliverables: ["SEO Roadmap", "Keyword Strategie", "Content Plan", "Technische SEO Plan"]
    },
    {
      step: "03",
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      title: "Implementatie & Optimalisatie",
      description: "We voeren alle SEO-optimalisaties uit, optimaliseren content en verbeteren de technische structuur.",
      duration: "5-10 dagen",
      deliverables: ["Content Optimalisatie", "Technische SEO", "Meta Tags", "Schema Markup"]
    },
    {
      step: "04",
      icon: <ChartLine className="w-8 h-8" weight="thin" />,
      title: "Monitoring & Groei",
      description: "Continue monitoring van je rankings, traffic en conversies met maandelijkse rapportages en optimalisaties.",
      duration: "Doorlopend",
      deliverables: ["Maandelijkse Rapportages", "Performance Monitoring", "Continue Optimalisatie", "Competitor Tracking"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ons <span className="text-blue-600">SEO proces</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van eerste analyse tot continue groei in 4 duidelijke stappen. 
            Transparant, data-gedreven en altijd gericht op resultaat.
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          {processSteps.map((step, index) => (
            <motion.div
              key={step.step}
              variants={stepVariants}
              className="relative"
            >
              {/* Connection Line */}
              {index < processSteps.length - 1 && (
                <div className="absolute left-8 top-20 w-0.5 h-32 bg-gray-200 hidden md:block"></div>
              )}
              
              <div className="flex flex-col md:flex-row items-start md:items-center mb-16 relative">
                {/* Step Number & Icon */}
                <div className="flex items-center mb-6 md:mb-0 md:mr-8">
                  <div className="relative">
                    <div className="w-16 h-16 bg-blue-900 rounded-2xl flex items-center justify-center text-white mb-4 md:mb-0">
                      {step.icon}
                    </div>
                                          <div className="absolute -top-3 -right-3 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {step.step}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>
                                          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                        Tijdsduur: {step.duration}
                      </div>
                    </div>

                    {/* Right Column - Deliverables */}
                    <div className="bg-gray-50 rounded-2xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Wat je ontvangt:</h4>
                      <div className="space-y-3">
                        {step.deliverables.map((deliverable, deliverableIndex) => (
                          <div key={deliverableIndex} className="flex items-center">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" weight="fill" />
                            <span className="text-gray-700 text-sm">{deliverable}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
                              <div className="text-4xl font-bold text-blue-600 mb-2">3-4</div>
              <div className="text-gray-600">Weken gemiddeld</div>
            </div>
            <div className="text-center">
                              <div className="text-4xl font-bold text-blue-600 mb-2">150%</div>
              <div className="text-gray-600">Traffic groei</div>
            </div>
            <div className="text-center">
                              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Monitoring</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeoProcess;
